import React, { useContext, useEffect, useState } from "react";
import { Button, InputAdornment, InputLabel, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormMoneyField } from "UI/FormMoneyField";
import { Info, InfoSection, Label } from "./RecipientDetails.style";
import { USDollar } from "utils/helpers";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";

const defaultFormData = {
  invoice_id: 0,
  quantity: 0,
  unit_price: 0,
  unit_price_adjusted: 0,
  total_price: 0,
  total_price_adjusted: 0,
};

export const EditPriceDialog = ({
  open,
  handleClose,
  invoice,
  record,
  refreshFn,
}) => {
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const { openConfirmationWithCallback } = useContext(GlobalContext);
  const [previousValue, setPreviousValue] = useState(null);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({
    values: record,
  });

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    const values = getValues();

    const { unit_price, unit_price_adjusted, quantity } = values;

    let totalAmountDelta = 0;

    if (Number(unit_price_adjusted) === Number(unit_price)) {
      values.unit_price_adjusted = 0;
      values.total_price_adjusted = 0;
    }

    if (Number(values.unit_price_adjusted) > 0) {
      values.unit_price_adjusted = Number(values.unit_price_adjusted);
      values.total_price_adjusted = unit_price_adjusted * quantity;
      if (Number(previousValue?.unit_price_adjusted) > 0) {
        totalAmountDelta =
          Number(previousValue?.total_price_adjusted) -
          Number(values.total_price_adjusted);
      } else {
        totalAmountDelta =
          Number(previousValue?.total_price) -
          Number(values.total_price_adjusted);
      }
    } else {
      // Reset adjusted price
      values.total_price_adjusted = 0;
      if (Number(previousValue?.unit_price_adjusted) > 0) {
        totalAmountDelta =
          Number(previousValue?.total_price_adjusted) -
          Number(values.total_price);
      } else {
        totalAmountDelta =
          Number(previousValue?.total_price) - Number(values.total_price);
      }
    }

    invoice.total_amount = Number(invoice.total_amount) - totalAmountDelta;

    const newInvoice = { ...invoice };
    delete newInvoice.invoice_date;
    delete newInvoice.paymentStatus;
    delete newInvoice.state;

    const body = {
      invoice: newInvoice,
      items: [values],
    };

    const result = await API.post("restApi", `/invoices/${invoice?.id}`, {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    refreshFn();
    forceCloseAndReset();
  });


  const closeAndReset = () => {
    if (isDirty) {
      openConfirmationWithCallback({
        title: "Warning",
        message: "The adjusted price in not saved. Proceed?",
        callbackFn: forceCloseAndReset,
        buttonLabels: ["No", "Yes"],
      });
      return;
    }

    forceCloseAndReset()
  };

  useEffect(() => {
    setPreviousValue({ ...record });
  }, [record, invoice]);

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={"Edit Price"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={3} minWidth={"320px"}>
            <InfoSection>
              <Info>
                <Label>Default price for {record?.item_description}: </Label>
                {USDollar.format(record?.unit_price ?? 0)}
              </Info>
            </InfoSection>
            <Stack alignItems={"flex-start"} flex={1}>
              <InputLabel htmlFor="unit_price_adjusted">
                Enter Adjusted Price:
              </InputLabel>
              <FormMoneyField
                id="unit_price_adjusted"
                name="unit_price_adjusted"
                control={control}
                InputProps={{
                  autoComplete: "new-password",
                  inputProps: {
                    min: 0,
                    max: 10000,
                    step: 0.01,
                  },
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>
        </form>
      }
      actions={
        <Stack alignItems={"center"} flex={1}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty}
          >
            Save changes
          </Button>
        </Stack>
      }
    />
  );
};
